import { useEffect, useRef, useState } from "react";
import { Copy, Download, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { PageHero } from "@/components/layout/PageHero";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const pageLink = (kind) => `${window.location.origin}/videos#video-${kind.toLowerCase()}`;

const SHARE = [
  { id: "whatsapp", label: "WhatsApp", href: (u, x) => `https://wa.me/?text=${encodeURIComponent(`${x} ${u}`)}` },
  { id: "facebook", label: "Facebook", href: (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}` },
  { id: "x", label: "X", href: (u, x) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(u)}&text=${encodeURIComponent(x)}` },
];

const ShareBox = ({ v, title }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const k = v.kind.toLowerCase();
  const url = pageLink(v.kind);
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, text: t("videos.shareText"), url }); return; } catch (e) { if (e?.name === "AbortError") return; }
    }
    setOpen((o) => !o);
  };
  const copy = () => navigator.clipboard?.writeText(url).then(() => toast.success(t("videos.copied"))).catch(() => toast.error(url));
  return (
    <div className="min-w-0 flex-1">
      <button type="button" onClick={share} className="btn-red btn-sm w-full justify-center" data-testid={`video-share-${k}`}><Share2 className="h-4 w-4" /> {t("videos.share")}</button>
      {open && (
        <div className="mt-2 flex min-w-0 flex-wrap gap-2" data-testid={`video-share-menu-${k}`}>
          {SHARE.map((s) => (
            <a key={s.id} href={s.href(url, t("videos.shareText"))} target="_blank" rel="noopener noreferrer"
              className="rounded-full border border-neutral-600 px-3 py-1.5 text-xs font-semibold text-neutral-100 hover:border-[var(--gold)]" data-testid={`video-share-${s.id}-${k}`}>{s.label}</a>
          ))}
          <button type="button" onClick={copy} className="inline-flex items-center gap-1 rounded-full border border-neutral-600 px-3 py-1.5 text-xs font-semibold text-neutral-100 hover:border-[var(--gold)]" data-testid={`video-share-copy-${k}`}>
            <Copy className="h-3.5 w-3.5" /> {t("videos.copy")}
          </button>
        </div>
      )}
    </div>
  );
};

const VideoCard = ({ v }) => {
  const { t } = useI18n();
  const k = v.kind.toLowerCase();
  const counted = useRef(false);
  const tk = t(`videos.title.${v.kind}`);
  const title = tk === `videos.title.${v.kind}` ? v.title : tk;
  const onPlay = () => {
    if (counted.current) return;
    counted.current = true;
    api.post(`/videos/${v.kind}/view`).catch(() => {});
  };
  return (
    <article id={`video-${k}`} className="card-carbon flex min-w-0 scroll-mt-24 flex-col gap-4 rounded-2xl p-4 sm:p-5" data-testid={`video-card-${k}`}>
      <h2 className="break-words font-display text-base font-bold uppercase tracking-wider text-[var(--gold)] md:text-lg" data-testid={`video-title-${k}`}>{title}</h2>
      <video className="mx-auto aspect-[9/16] w-full max-w-[360px] rounded-xl border border-neutral-800 bg-black object-cover"
        src={`${BACKEND}${v.video}`} poster={`${BACKEND}${v.cover}`} controls playsInline preload="none" onPlay={onPlay} data-testid={`video-player-${k}`} />
      <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
        <a className="btn-gold btn-sm flex-1 justify-center" href={`${BACKEND}${v.video}?download=1`} download={v.filename} data-testid={`video-download-${k}`}>
          <Download className="h-4 w-4" /> {t("videos.download")}
        </a>
        <ShareBox v={v} title={title} />
      </div>
    </article>
  );
};

export default function Videos() {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/videos").then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);
  useEffect(() => {
    if (items?.length && window.location.hash) document.querySelector(window.location.hash)?.scrollIntoView();
  }, [items]);
  return (
    <div data-testid="videos-page">
      <PageHero eyebrow="FAB-MIND-OS" title={t("videos.title")}>
        <p className="mt-4 max-w-2xl text-sm text-neutral-300" data-testid="videos-free-line">{t("videos.free")}</p>
      </PageHero>
      <div className="mx-auto max-w-6xl px-4 pb-16 sm:px-8">
        {!items && <Loader2 className="h-6 w-6 animate-spin text-[var(--gold)]" data-testid="videos-loading" />}
        {items && !items.length && <p className="card-carbon rounded-2xl p-5 text-sm text-neutral-400" data-testid="videos-empty">{t("videos.empty")}</p>}
        {items?.length > 0 && <div className="grid gap-6 md:grid-cols-2" data-testid="videos-list">{items.map((v) => <VideoCard key={v.kind} v={v} />)}</div>}
      </div>
    </div>
  );
}
